import { useState } from 'react';
import { HighlightedCode } from '../HighlightedCode';
import { FileDiffView } from '../FileDiffView';
import { ToolPayloadView } from './ToolPayloadView';
import { inferLanguageFromPath } from './constants';

type EditOutcome = {
  index: number;
  status: 'applied' | 'not-found' | 'ambiguous' | 'conflict' | 'recovered' | 'syntax-rejected';
  matchStart?: number;
  matchEnd?: number;
};

type EditArgsEntry = { search?: string; replacement?: string };

export function computeFileContentAfter(
  before: string,
  args?: Record<string, unknown>,
  result?: Record<string, unknown>,
): string | undefined {
  if (!args) return undefined;
  const search = typeof args.search === 'string' ? (args.search as string) : undefined;
  const replacement = typeof args.replacement === 'string' ? (args.replacement as string) : undefined;
  const edits = Array.isArray(args.edits) ? (args.edits as EditArgsEntry[]) : undefined;

  if (edits && edits.length > 0) {
    const outcomes = Array.isArray(result?.outcomes) ? (result!.outcomes as EditOutcome[]) : [];
    const applied = outcomes.filter(
      o => (o.status === 'applied' || o.status === 'recovered')
        && typeof o.matchStart === 'number' && typeof o.matchEnd === 'number'
        && edits[o.index] != null,
    );
    if (applied.length > 0) {
      const sorted = [...applied].sort((a, b) => (b.matchStart! - a.matchStart!));
      let content = before;
      for (const outcome of sorted) {
        const edit = edits[outcome.index];
        const rep = typeof edit?.replacement === 'string' ? edit.replacement : '';
        content = content.slice(0, outcome.matchStart!) + rep + content.slice(outcome.matchEnd!);
      }
      return content;
    }
    let content = before;
    for (const edit of edits) {
      if (typeof edit.search === 'string' && edit.search.length > 0 && typeof edit.replacement === 'string') {
        const idx = content.indexOf(edit.search);
        if (idx !== -1) {
          content = content.slice(0, idx) + edit.replacement + content.slice(idx + edit.search.length);
        }
      }
    }
    return content;
  }

  if (typeof replacement === 'string') {
    if (!search) return replacement;
    const idx = before.indexOf(search);
    if (idx === -1) return undefined;
    return before.slice(0, idx) + replacement + before.slice(idx + search.length);
  }

  return undefined;
}

export function ToolCallDetails({ toolInput, toolResult, fileContentBefore, fallbackErrorMessage }: {
  toolInput?: Record<string, unknown>;
  toolResult?: Record<string, unknown>;
  fileContentBefore?: string;
  /** Used when toolResult is missing (e.g. event dropped) but the row is known to be a failure. */
  fallbackErrorMessage?: string;
}) {
  const filePath = typeof toolInput?.path === 'string' ? (toolInput.path as string)
    : typeof toolResult?.path === 'string' ? (toolResult.path as string)
    : undefined;
  const fileContentAfter = fileContentBefore != null
    ? computeFileContentAfter(fileContentBefore, toolInput, toolResult)
    : undefined;
  const isNewFile = fileContentAfter != null && (!fileContentBefore || fileContentBefore.length === 0);
  const hasDiff = fileContentAfter != null;

  // Failure detection: explicit `success: false` on the result, OR a
  // fallback message was passed because the wire dropped the result entirely.
  const isFailure = toolResult?.success === false || (!!fallbackErrorMessage && !toolResult);
  // Pull a human-readable error string: prefer the typed `error` field,
  // then `message`, then the fallback. The tail dropdown shows the same
  // text under the Result tab — the banner just makes it impossible to miss.
  const errorText: string | undefined = isFailure
    ? (typeof toolResult?.error === 'string' ? (toolResult!.error as string)
       : typeof toolResult?.message === 'string' ? (toolResult!.message as string)
       : fallbackErrorMessage)
    : undefined;

  const [activeTab, setActiveTab] = useState<'diff' | 'input' | 'result' | 'fileBefore'>(
    // On failure, default to 'result' so the error JSON is one click away
    // from the banner — Input is rarely what the user wants to see first
    // when something failed.
    isFailure ? 'result' : hasDiff ? 'diff' : 'input',
  );

  const tabs: Array<{ key: 'diff' | 'input' | 'result' | 'fileBefore'; label: string; available: boolean }> = [
    { key: 'diff', label: isNewFile ? 'New File' : 'Diff', available: hasDiff },
    { key: 'input', label: 'Input', available: !!toolInput },
    { key: 'result', label: 'Result', available: !!toolResult },
    { key: 'fileBefore', label: 'File Before', available: !!fileContentBefore },
  ];

  const effectiveTab = tabs.find(t => t.key === activeTab && t.available)
    ? activeTab
    : tabs.find(t => t.available)?.key ?? 'input';

  const diffLanguage = inferLanguageFromPath(filePath);

  return (
    <div className="tool-call-details" data-testid="tool-call-details">
      {isFailure && errorText && (
        <div className="tool-error-banner" data-testid="tool-error-banner">
          <span className="tool-error-banner-label">Error</span>
          <pre className="tool-error-banner-message">{errorText}</pre>
        </div>
      )}
      <div className="tool-detail-tabs">
        {tabs.filter(t => t.available).map(t => (
          <button
            key={t.key}
            className={`tool-detail-tab${effectiveTab === t.key ? ' active' : ''}`}
            onClick={(e) => { e.stopPropagation(); setActiveTab(t.key); }}
          >
            {t.label}
          </button>
        ))}
      </div>
      <div className="tool-detail-content">
        {effectiveTab === 'diff' && hasDiff && (
          <FileDiffView
            before={fileContentBefore ?? ''}
            after={fileContentAfter!}
            path={filePath}
            language={diffLanguage}
            isNewFile={isNewFile}
          />
        )}
        {effectiveTab === 'input' && toolInput && (
          <ToolPayloadView payload={toolInput} />
        )}
        {effectiveTab === 'result' && toolResult && (
          <ToolPayloadView payload={toolResult} />
        )}
        {effectiveTab === 'fileBefore' && fileContentBefore && (
          <HighlightedCode
            code={fileContentBefore}
            language={diffLanguage}
            filename={filePath}
            className="tool-detail-code"
          />
        )}
      </div>
    </div>
  );
}
